/**
 * Modelo Flyer V2 — resuelve precio, branding, imagen y promo sin inventar datos.
 * Subject-agnóstico: acepta producto (y mañana combo) con shape mínima.
 */
import {
  buildEmpresaContacto,
  formatCurrencyAr,
  resolveListaPrecio
} from '../../../utils/catalogoComercialExport';
import {
  resolvePrecioAnterior,
  resolveExpresPrice
} from '../../../utils/listasExpresCatalog';
import { computeAhorro } from '../../../utils/listasExpresAhorro';

export const DEFAULT_FLYER_BRAND = {
  primary: '#0F766E',
  secondary: '#F59E0B'
};

export function resolveProductImageUrl(product, overrideUrl) {
  if (overrideUrl && String(overrideUrl).trim()) return String(overrideUrl).trim();
  if (!product) return '';
  return product.imagen_url || product.imagen || product.foto || '';
}

export function resolveFlyerBrandColors(empresaConfig = {}, overrides = {}) {
  const cfgPrimary =
    empresaConfig?.color_marca_primario ||
    empresaConfig?.marca?.color_primario ||
    empresaConfig?.tema?.color_primario_hex ||
    '';
  const cfgSecondary =
    empresaConfig?.color_marca_secundario ||
    empresaConfig?.marca?.color_secundario ||
    empresaConfig?.tema?.color_secundario_hex ||
    '';
  return {
    primary: overrides.primary || cfgPrimary || DEFAULT_FLYER_BRAND.primary,
    secondary: overrides.secondary || cfgSecondary || DEFAULT_FLYER_BRAND.secondary,
    fromConfig: Boolean(cfgPrimary || cfgSecondary),
    defaultsUsed: !cfgPrimary && !cfgSecondary && !overrides.primary && !overrides.secondary
  };
}

export function resolveFlyerPrice(product, priceListKey = 'mayorista') {
  if (!product) return { amount: null, formatted: null, listKey: priceListKey };
  const amount = resolveExpresPrice(product, priceListKey);
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) {
    return { amount: null, formatted: null, listKey: priceListKey };
  }
  return { amount: n, formatted: formatCurrencyAr(n), listKey: priceListKey };
}

export function resolveFlyerCompare(product, priceListKey = 'mayorista') {
  const price = resolveFlyerPrice(product, priceListKey);
  const anterior = resolvePrecioAnterior(product);
  const ahorro = computeAhorro(price.amount, anterior);
  if (!ahorro) {
    return {
      showCompare: false,
      precioAnterior: null,
      precioAnteriorFmt: null,
      pct: null,
      monto: null
    };
  }
  return {
    showCompare: true,
    precioAnterior: ahorro.precioAnterior,
    precioAnteriorFmt: formatCurrencyAr(ahorro.precioAnterior),
    pct: ahorro.pct,
    monto: ahorro.monto
  };
}

/**
 * Normaliza respuesta de promociones.aplicar para un ítem.
 * Solo expone badge/precio si el motor devolvió datos fiables.
 */
export function normalizePromoFromAplicar(item) {
  if (!item || typeof item !== 'object') return null;
  const promos = Array.isArray(item.promociones) ? item.promociones : [];
  const first = promos[0] || null;
  const descuento = Number(item.descuento_promocion ?? item.descuento ?? 0);
  const precioFinal = Number(item.precio_final);
  const precioBase = Number(item.precio);
  const hasDiscount = Number.isFinite(descuento) && descuento > 0;
  const hasFinal =
    Number.isFinite(precioFinal) &&
    Number.isFinite(precioBase) &&
    precioFinal > 0 &&
    precioFinal < precioBase;

  if (!first && !hasDiscount && !hasFinal) return null;

  const nombre = first?.nombre || first?.titulo || item.mensaje_promocion || 'Promoción';
  const vigencia =
    first?.fecha_fin || first?.vigencia_hasta || item.vigencia_hasta || first?.hasta || '';

  let pct = null;
  if (hasFinal && precioBase > 0) {
    pct = Math.round(((precioBase - precioFinal) / precioBase) * 1000) / 10;
  } else if (
    first?.tipo === 'porcentaje' &&
    Number.isFinite(Number(first?.valor)) &&
    Number(first.valor) > 0
  ) {
    pct = Number(first.valor);
  }

  return {
    nombre: String(nombre),
    badge: pct != null ? `${pct}% OFF` : String(nombre).slice(0, 24),
    precioBase: Number.isFinite(precioBase) ? precioBase : null,
    precioBaseFmt: Number.isFinite(precioBase) ? formatCurrencyAr(precioBase) : null,
    precioFinal: hasFinal ? precioFinal : null,
    precioFinalFmt: hasFinal ? formatCurrencyAr(precioFinal) : null,
    vigencia: vigencia ? String(vigencia).slice(0, 32) : '',
    mensaje: item.mensaje_promocion || first?.descripcion || ''
  };
}

export function buildFlyerSubjectFromProduct(product, { priceListKey = 'mayorista', overrideImage = '' } = {}) {
  if (!product) return null;
  const imageUrl = resolveProductImageUrl(product, overrideImage);
  const price = resolveFlyerPrice(product, priceListKey);
  const compare = resolveFlyerCompare(product, priceListKey);
  return {
    kind: 'product',
    id: product.id,
    name: product.nombre || 'Producto',
    code: product.codigo || '',
    imageUrl,
    hasImage: Boolean(imageUrl),
    price,
    compare,
    raw: product
  };
}

export function buildFlyerBrand(empresaConfig, { colors, showLogo, showContact, showQr } = {}) {
  const contacto = buildEmpresaContacto(empresaConfig || {});
  const brandColors = resolveFlyerBrandColors(empresaConfig, colors || {});
  return {
    ...contacto,
    colors: brandColors,
    showLogo: showLogo !== false && Boolean(contacto.logo),
    showName: true,
    showContact: showContact !== false,
    showQr: showQr !== false && Boolean(contacto.qrImageUrl)
  };
}

export { buildEmpresaContacto, formatCurrencyAr, resolveListaPrecio };
